import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Users, Shield, UserX, Key, LogOut, TrendingUp, 
  Clock, Search, Eye, AlertCircle, CheckCircle, XCircle
} from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Dashboard data
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loginLogs, setLoginLogs] = useState([]);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    // Check if admin is already logged in
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsAuthenticated(true);
      fetchDashboardData();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axiosInstance.post('/admin/login', {
        username,
        password
      });
      
      localStorage.setItem('admin_token', response.data.access_token);
      setIsAuthenticated(true);
      toast.success('Admin-Login erfolgreich!');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setUsers([]);
    setStats(null);
    toast.info('Abgemeldet');
  };

  const fetchDashboardData = async () => {
    try {
      // Get stats
      const statsRes = await axiosInstance.get('/admin/stats', {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setStats(statsRes.data);

      // Get users
      const usersRes = await axiosInstance.get('/admin/users', {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setUsers(usersRes.data.users);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    }
  };

  const fetchLoginLogs = async (userId) => {
    try {
      const response = await axiosInstance.get(`/admin/login-logs/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setLoginLogs(response.data.logs);
    } catch (error) {
      toast.error('Fehler beim Laden der Login-Logs');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await axiosInstance.post('/admin/reset-user-password', 
        { user_id: userId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      
      setNewPassword(response.data.new_password);
      setShowPasswordDialog(true);
      toast.success('Passwort erfolgreich zurückgesetzt!');
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen des Passworts');
    }
  };

  const handleToggleUserStatus = async (userId) => {
    try {
      const response = await axiosInstance.post('/admin/toggle-user-status',
        { user_id: userId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }
      );
      
      toast.success(`User ${response.data.status === 'active' ? 'entsperrt' : 'gesperrt'}`);
      fetchDashboardData();
    } catch (error) {
      toast.error('Fehler beim Ändern des User-Status');
    }
  };

  const filteredUsers = users.filter(user => 
    user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Shield className="w-6 h-6 text-indigo-600" />
              Admin-Login
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Benutzername</label>
                <Input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Passwort</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Wird geladen...' : 'Anmelden'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-6 px-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-sm text-indigo-100">FinAds Creator</p>
            </div>
          </div>
          <Button 
            onClick={handleLogout}
            variant="outline"
            className="bg-white/10 border-white/20 hover:bg-white/20 text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Abmelden
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Gesamt User</p>
                    <p className="text-3xl font-bold text-indigo-600">{stats.total_users}</p>
                  </div>
                  <Users className="w-10 h-10 text-indigo-600 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Lifetime User</p>
                    <p className="text-3xl font-bold text-green-600">{stats.lifetime_users}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-green-600 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Gesperrt</p>
                    <p className="text-3xl font-bold text-red-600">{stats.blocked_users}</p>
                  </div>
                  <UserX className="w-10 h-10 text-red-600 opacity-20" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Letzte 7 Tage</p>
                    <p className="text-3xl font-bold text-blue-600">{stats.recent_registrations}</p>
                  </div>
                  <Clock className="w-10 h-10 text-blue-600 opacity-20" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* User Table */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-xl">Alle Benutzer ({filteredUsers.length})</CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Suche User..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Abo-Typ</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Logins</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Letzter Login</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{user.username}</div>
                        <div className="text-xs text-gray-500">{user.id?.substring(0, 8)}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                      <td className="px-4 py-3">
                        <Badge variant={user.subscription_type === 'lifetime' ? 'default' : 'secondary'}>
                          {user.subscription_type || 'none'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {user.is_active === false ? (
                          <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                            <XCircle className="w-3 h-3" />
                            Gesperrt
                          </Badge>
                        ) : (
                          <Badge variant="default" className="flex items-center gap-1 w-fit bg-green-500">
                            <CheckCircle className="w-3 h-3" />
                            Aktiv
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{user.login_count || 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {user.last_login ? new Date(user.last_login).toLocaleString('de-DE') : 'Nie'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedUser(user);
                              fetchLoginLogs(user.id);
                              setShowLogsDialog(true);
                            }}
                            title="Login-Logs anzeigen"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResetPassword(user.id)}
                            title="Passwort zurücksetzen"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={user.is_active === false ? "default" : "destructive"}
                            onClick={() => handleToggleUserStatus(user.id)}
                            title={user.is_active === false ? "User entsperren" : "User sperren"}
                          >
                            {user.is_active === false ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Login Logs Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Login-Protokoll: {selectedUser?.username}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {loginLogs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Keine Login-Logs vorhanden</p>
            ) : (
              <div className="space-y-2">
                {loginLogs.map((log, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-sm">
                          {new Date(log.timestamp).toLocaleString('de-DE', {
                            dateStyle: 'full',
                            timeStyle: 'medium'
                          })}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">IP: {log.ip_address}</p>
                        <p className="text-xs text-gray-500 mt-1">{log.user_agent}</p>
                      </div>
                      <Badge variant="secondary">Login #{loginLogs.length - idx}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neues Passwort generiert</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 mb-2">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Bitte speichere dieses Passwort sicher. Es wird nur einmal angezeigt!
              </p>
              <div className="bg-white p-3 rounded border border-yellow-300 mt-2">
                <p className="font-mono text-lg font-bold text-center">{newPassword}</p>
              </div>
            </div>
            <Button 
              onClick={() => {
                navigator.clipboard.writeText(newPassword);
                toast.success('Passwort in Zwischenablage kopiert!');
              }}
              className="w-full"
            >
              In Zwischenablage kopieren
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
